'use client';
import { useAskInputInstance } from '#pplx/frontend/apps/ask/ask-input/ask-input-core/hooks/useAskInputInstance.js';
import { useAskInputRootState } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputRootShared.js';
import { QUERY_SUBMIT_DEBOUNCE_MS } from '#pplx/frontend/apps/ask/ask-input/ask-input-types/index.js';
import { buildSlashCommandDisplayMetadata } from '#pplx/frontend/apps/ask/commands/buildSlashCommandDisplayMetadata.js';
import { useAvailableSlashCommands } from '#pplx/frontend/apps/ask/commands/hooks/useAvailableSlashCommands.js';
import { consumePendingComputerPrimer } from '#pplx/frontend/apps/ask/computer/ask-input/pendingComputerPrimer.js';
import { useDeepResearchChipRemovalAnalytics } from '#pplx/frontend/apps/ask/hooks/ask-deep-research-hooks/useDeepResearchChipRemovalAnalytics.js';
import { useComputerBeforeSubmit } from '#pplx/frontend/apps/ask/hooks/computer-before-submit/useComputerBeforeSubmit.js';
import { useComputerTaskSubmitter } from '#pplx/frontend/apps/ask/hooks/computer-hooks/useComputerTaskSubmitter.js';
import { useNativeBridgeActions } from '#pplx/frontend/apps/ask/native-bridges/store.js';
import { getRemixStartAttribution } from '#pplx/frontend/apps/ask/shared-ui-components/remixAssetAttachments.js';
import { LOCAL_MODEL_PREFERENCE } from '#pplx/frontend/apps/ask/thread-utils/computerThreadActions.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { ModalOrigin } from '#pplx/frontend/components/types/types.js';
import { FROM_COMPUTER_UPSELL_PARAM } from '#pplx/frontend/components/utils/consts.js';
import { EVENT_PAYLOAD } from '#pplx/frontend/framework/instrumentation/types.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useCallback, useMemo, useRef } from 'react';
const computerPrimerQuerySubmittedEvent = EVENT_PAYLOAD;
const computerAssetRemixStartedEvent = EVENT_PAYLOAD;
export function useComputerAskInput(args) {
    const { workspaceDirectories } = useAskInputRootState();
    const onSubmit = args.onSubmit;
    const disabled = args.disabled ?? false;
    const searchParams = useSearchParams();
    // Keep the upsell source with this prefill route, including blocked submit retries.
    const fromUpsell = searchParams.get(FROM_COMPUTER_UPSELL_PARAM) === '1';
    const beforeSubmit = useComputerBeforeSubmit({
        isLocalMode: args.searchModel === LOCAL_MODEL_PREFERENCE,
    });
    const { track } = useInstrumentation();
    const { taskCreated, taskFirstMessageReceived } = useNativeBridgeActions();
    const { commands: slashCommands } = useAvailableSlashCommands();
    const { submitComputerTask } = useComputerTaskSubmitter({
        basePath: args.basePath,
        modalOrigin: ModalOrigin.COMPUTER_SUBMIT_QUERY,
        navigateLabel: 'Computer ask input',
        onBeforeNavigate: args.onBeforeNavigate,
        searchModel: args.searchModel,
        targetCollection: args.targetCollection,
        beforeSubmit,
        onTaskCreated: taskCreated
            ? (data) => {
                taskCreated(data)?.catch(() => { });
            }
            : undefined,
        onFirstMessageReceived: taskFirstMessageReceived
            ? (data) => {
                taskFirstMessageReceived(data)?.catch(() => { });
            }
            : undefined,
    });
    const handleSubmitQuery = useCallback((params) => submitComputerTask({
        query: params.query ?? '',
        dslQuery: params.dslQuery,
        mentions: params.mentions,
        prefills: params.prefills,
        attachments: params.attachments,
        queryDisplayMetadata: buildSlashCommandDisplayMetadata(params.slashCommand, params.dslQuery, slashCommands, params.slashCommandSubmittedLabel),
        promptSource: params.promptSource ?? 'user',
        querySource: params.querySource,
        fromUpsell,
        timeFromFirstType: params.timeFromFirstType,
        browserAgentAllowOnceFromToggle: params.browserAgentAllowOnceFromToggle,
        forceEnableBrowserAgent: params.forceEnableBrowserAgent,
        canonicalPageContext: params.canonicalPageContext,
        newFrontendContextUUID: params.newFrontendContextUUID,
        workspaceDirectories,
        localFileGrants: params.localFileGrants,
        sourcesOverride: params.sourcesOverride,
        modeConfig: params.modeConfig ?? args.modeConfig,
        workflowConfig: args.workflowConfig,
    }).sessionStarted, [
        args.modeConfig,
        args.workflowConfig,
        fromUpsell,
        submitComputerTask,
        slashCommands,
        workspaceDirectories,
    ]);
    const isSubmittingRef = useRef(false);
    const debouncedOnSubmit = useCallback((params) => {
        if (disabled || isSubmittingRef.current)
            return false;
        isSubmittingRef.current = true;
        setTimeout(() => (isSubmittingRef.current = false), QUERY_SUBMIT_DEBOUNCE_MS);
        const didSubmit = handleSubmitQuery(params);
        if (!didSubmit) {
            return false;
        }
        const remixAttribution = getRemixStartAttribution(params.fileAttachments);
        if (remixAttribution) {
            track(computerAssetRemixStartedEvent({
                name: 'computer asset remix started',
                data: remixAttribution,
            }));
        }
        const pendingPrimer = consumePendingComputerPrimer();
        if (pendingPrimer) {
            const submittedQuery = params.query ?? '';
            const hasUserMention = params.mentions?.some((mention) => mention.type === 'user') ?? false;
            track(computerPrimerQuerySubmittedEvent({
                name: 'computer primer query submitted',
                data: {
                    primerCategory: pendingPrimer.primerCategory,
                    primerCategoryUuid: pendingPrimer.primerCategoryUuid ?? null,
                    previewText: pendingPrimer.previewText,
                    executionPrompt: pendingPrimer.executionPrompt,
                    ...(!hasUserMention && {
                        submittedQuery,
                        wasPrefillEdited: submittedQuery !== pendingPrimer.executionPrompt,
                    }),
                    isPersonalized: pendingPrimer.isPersonalized,
                    querySource: pendingPrimer.querySource ?? null,
                },
            }));
        }
        onSubmit?.(params);
        return true;
    }, [disabled, handleSubmitQuery, onSubmit, track]);
    const [value, json, actions] = useAskInputInstance({
        onSubmit: debouncedOnSubmit,
        immediate: false,
    });
    const onChange = useDeepResearchChipRemovalAnalytics({
        onChange: actions.onChange,
        searchMode: SearchMode.ASI,
    });
    return [value, json, useMemo(() => ({ ...actions, onChange }), [actions, onChange])];
}
//# sourceMappingURL=useComputerAskInput.js.map